const BASE = '/api/v1';

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const data = await res.json().catch(() => ({ error: { message: res.statusText } }));

  if (!res.ok) {
    // Backend returns { error: { code, message, details } }
    const errPayload = data?.error;
    const message =
      typeof errPayload === 'string'
        ? errPayload
        : (errPayload?.message ?? 'Unknown error');

    // The vendor panel has its own auth and is independent of the customer
    // session. If the admin is signed in to /vendor while their personal
    // customer session has expired (common: they own a tenant they revoked
    // for testing), we must NOT bounce them out of /vendor to the customer
    // license-expired or login page.
    const onVendorPath = window.location.pathname.startsWith('/vendor');

    // Cookie expired or invalid — wipe local session and redirect to login
    if (res.status === 401 && !onVendorPath) {
      localStorage.removeItem('vmt_user');
      localStorage.removeItem('vmt_timezone');
      window.location.href = '/login';
    }

    if (res.status === 402 && !onVendorPath && !window.location.pathname.startsWith('/license-expired')) {
      window.location.href = '/license-expired';
    }

    throw new ApiError(res.status, message);
  }

  return data as T;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export const api = {
  get:    <T>(path: string)               => request<T>('GET',    path),
  post:   <T>(path: string, body: unknown) => request<T>('POST',   path, body),
  patch:  <T>(path: string, body: unknown) => request<T>('PATCH',  path, body),
  delete: <T>(path: string)               => request<T>('DELETE', path),
};

// ── Auth ─────────────────────────────────────────────────────────────────────
export interface LoginResponse {
  timezone: string;
  user: { id: string; email: string; full_name: string; role: Role; email_notifications: boolean };
}
export type Role = 'admin' | 'supervisor' | 'technician';

export const authApi = {
  login:    (email: string, password: string, timezone?: string) =>
    api.post<LoginResponse>('/auth/login', { email, password, timezone }),
  logout:   () => api.post<{ message: string }>('/auth/logout', {}),
  register: (company_name: string, full_name: string, email: string, password: string) =>
    api.post<{ message: string; requires_verification: boolean }>('/auth/register', { company_name, full_name, email, password }),
  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post<{ message: string }>('/auth/reset-password', { token, password }),
};

// ── Equipment ─────────────────────────────────────────────────────────────────
export interface Equipment {
  id: string; asset_tag: string; name: string; type: string;
  location: string; criticality: string; manufacturer?: string;
  model?: string; serial_number?: string; install_date?: string;
  notes?: string | null;
  current_reading?: number | null;
  meter_unit?: string | null;
  is_active: boolean; tenant_id: string; created_at: string; updated_at: string;
}

export interface MeterReading {
  id: string;
  equipment_id: string;
  value: number;
  note: string | null;
  recorded_by: string;
  recorded_at: string;
}
export interface Paginated<T> { data: T[]; pagination: { page: number; page_size: number; total: number } }

export interface EquipmentStats {
  total_work_orders: number;
  open_work_orders: number;
  total_downtime_minutes: number;
  total_cost: number;
}

export interface EquipmentDetail extends Equipment {
  stats: EquipmentStats;
  maintenance_tasks: MaintenanceTask[];
  recent_work_orders: WorkOrder[];
}

// ── Generic 4-step import types (Equipment + Parts) ──────────────────────────
// The PM import has its own (older) types above; Equipment + Parts share these
// generic shapes parameterized by their resolved-row type.
export interface ImportSummary { ready: number; warnings: number; errors: number; duplicates: number; }
export interface GenericPreviewRow<R> {
  row_index: number;
  status:    'ready' | 'warning' | 'error' | 'duplicate';
  resolved?: R;
  error?:    string;
  warnings?: string[];
}
export interface GenericPreviewResponse<R> { rows: GenericPreviewRow<R>[]; summary: ImportSummary; }
export interface GenericImportCommitResponse { created: number; errors: Array<{ row_index: number; error: string }>; }

export interface EquipmentImportPreviewRow {
  row_index:     number;
  asset_tag?:    string;
  name:          string;
  type:          string;
  location:      string;
  criticality?:  string;
  manufacturer?: string;
  model?:        string;
  serial_number?: string;
  install_date?: string;
  is_active?:    boolean;
}
export interface EquipmentImportResolved {
  asset_tag:       string | null;
  equipment_label: string;
  name:            string;
  type:            string;
  location:        string;
  criticality:     string;
  manufacturer?:   string;
  model?:          string;
  serial_number?:  string;
  install_date:    string | null;
  is_active:       boolean;
}
export type EquipmentImportCommitRow = EquipmentImportResolved & { row_index: number };

export interface PartImportPreviewRow {
  row_index:          number;
  part_number:        string;
  name:               string;
  description?:       string;
  location?:          string;
  supplier?:          string;
  stock_quantity?:    string;
  reorder_threshold?: string;
  unit_cost?:         string;
}
export interface PartImportResolved {
  part_number:       string;
  name:              string;
  description?:      string;
  location?:         string;
  supplier?:         string;
  stock_quantity:    number;
  reorder_threshold: number;
  unit_cost:         number | null;
}
export type PartImportCommitRow = PartImportResolved & { row_index: number };

export const equipmentApi = {
  list:   (page = 1, page_size = 50, search?: string, sort?: string, dir?: 'asc' | 'desc') => {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    if (search) params.set('search', search);
    if (sort)   params.set('sort',   sort);
    if (dir)    params.set('dir',    dir);
    return api.get<Paginated<Equipment>>(`/equipment?${params}`);
  },
  get:    (id: string) => api.get<Equipment>(`/equipment/${id}`),
  detail: (id: string) => api.get<EquipmentDetail>(`/equipment/${id}/detail`),
  create: (body: Partial<Equipment>) => api.post<Equipment>('/equipment', body),
  update: (id: string, body: Partial<Equipment>) => api.patch<Equipment>(`/equipment/${id}`, body),
  delete: (id: string) => api.delete<void>(`/equipment/${id}`),
  logMeterReading: (id: string, body: { value: number; note?: string }) =>
    api.post<MeterReading>(`/equipment/${id}/meter-reading`, body),
  listMeterReadings: (id: string, page = 1, page_size = 50) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    return api.get<{ data: MeterReading[]; meta: { page: number; page_size: number; total: number } }>(`/equipment/${id}/meter-readings?${params}`);
  },
  importPreview: (rows: EquipmentImportPreviewRow[]) =>
    api.post<GenericPreviewResponse<EquipmentImportResolved>>('/equipment/import-preview', { rows }),
  importCommit:  (rows: EquipmentImportCommitRow[]) =>
    api.post<GenericImportCommitResponse>('/equipment/import-commit', { rows }),
};

// ── Parts ─────────────────────────────────────────────────────────────────────
export interface Part {
  id: string; part_number: string; name: string; description?: string;
  unit_cost?: number; stock_quantity: number; reorder_threshold?: number;
  location?: string; supplier?: string; tenant_id: string;
  created_at: string; updated_at: string;
}

export const partsApi = {
  list:   (page = 1, page_size = 50, search?: string, sort?: string, dir?: 'asc' | 'desc') => {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    if (search) params.set('search', search);
    if (sort)   params.set('sort',   sort);
    if (dir)    params.set('dir',    dir);
    return api.get<Paginated<Part>>(`/parts?${params}`);
  },
  get:    (id: string) => api.get<Part>(`/parts/${id}`),
  create: (body: Partial<Part>) => api.post<Part>('/parts', body),
  update: (id: string, body: Partial<Part>) => api.patch<Part>(`/parts/${id}`, body),
  delete: (id: string) => api.delete<void>(`/parts/${id}`),
  importPreview: (rows: PartImportPreviewRow[]) =>
    api.post<GenericPreviewResponse<PartImportResolved>>('/parts/import-preview', { rows }),
  importCommit:  (rows: PartImportCommitRow[]) =>
    api.post<GenericImportCommitResponse>('/parts/import-commit', { rows }),
};

// ── Maintenance Tasks ─────────────────────────────────────────────────────────
export interface MaintenanceTask {
  id: string; name: string; task_type: string; frequency_type: string;
  frequency_value?: number | null;
  estimated_duration_minutes?: number | null;
  instructions?: string | null;
  last_completed_at?: string | null;
  next_due_at?: string | null;
  schedule_type?: 'time' | 'meter';
  meter_interval?: number | null;
  last_trigger_reading?: number | null;
  is_active: boolean; equipment_id: string;
  assigned_technician_id?: string | null;
  compliance_window_days?: number | null;
  month_week?: number | null;
  month_weekday?: number | null;
  pm_group_id?: string | null;
  tenant_id: string; created_at: string; updated_at: string;
  equipment?: Pick<Equipment, 'id' | 'asset_tag' | 'name' | 'location' | 'current_reading' | 'meter_unit'>;
  assigned_technician?: Pick<User, 'id' | 'full_name' | 'email'>;
}

// ── PM Import types ──────────────────────────────────────────────────────────

export interface ImportPreviewRow {
  row_index:                  number;
  equipment_ref:              string;
  name:                       string;
  frequency_phrase:           string;
  task_type?:                 string;
  technician_ref?:            string;
  estimated_duration_minutes?: number;
  instructions?:              string;
  last_completed_at?:         string;
  next_due_at?:               string;
  is_active?:                 boolean;
}

export interface ImportCommitRow {
  row_index:                  number;
  equipment_id:               string;
  name:                       string;
  task_type:                  'preventive' | 'predictive' | 'corrective';
  frequency_type:             'days' | 'weeks' | 'months' | 'hours_run' | 'on_condition';
  frequency_value:            number | null;
  schedule_type:              'time' | 'meter';
  meter_interval?:            number;
  assigned_technician_id?:    string;
  estimated_duration_minutes?: number;
  instructions?:              string;
  last_completed_at?:         string;
  next_due_at?:               string;
  is_active:                  boolean;
}

export interface PreviewRowResult {
  row_index: number;
  status:    'ready' | 'warning' | 'error' | 'duplicate';
  resolved?: ImportCommitRow & { equipment_label: string; assigned_technician_label?: string };
  error?:    string;
  warnings?: string[];
}

export interface ImportPreviewResponse {
  rows: PreviewRowResult[];
  summary: { ready: number; warnings: number; errors: number; duplicates: number };
}

export interface ImportCommitResponse {
  created: number;
  errors:  Array<{ row_index: number; error: string }>;
}

export const tasksApi = {
  list: (page = 1, page_size = 50, equipment_id?: string, task_type?: string, is_active?: boolean, search?: string) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    if (equipment_id) params.set('equipment_id', equipment_id);
    if (task_type) params.set('task_type', task_type);
    if (is_active !== undefined) params.set('is_active', String(is_active));
    if (search) params.set('search', search);
    return api.get<Paginated<MaintenanceTask>>(`/maintenance-tasks?${params}`);
  },
  due:         () => api.get<{ data: MaintenanceTask[] }>('/maintenance-tasks/due'),
  get:         (id: string) => api.get<MaintenanceTask>(`/maintenance-tasks/${id}`),
  create:      (body: Partial<MaintenanceTask>) => api.post<MaintenanceTask>('/maintenance-tasks', body),
  createGroup: (body: Partial<MaintenanceTask> & { equipment_ids: string[] }) =>
    api.post<{ pm_group_id: string | null; count: number; tasks: MaintenanceTask[] }>('/maintenance-tasks/group', body),
  updateGroup: (groupId: string, body: Partial<MaintenanceTask>) =>
    api.patch<{ count: number; tasks: MaintenanceTask[] }>(`/maintenance-tasks/group/${groupId}`, body),
  update:      (id: string, body: Partial<MaintenanceTask>) =>
    api.patch<MaintenanceTask>(`/maintenance-tasks/${id}`, body),
  delete:      (id: string) => api.delete<void>(`/maintenance-tasks/${id}`),
  createWO:    (id: string) => api.post<WorkOrder>(`/maintenance-tasks/${id}/create-work-order`, {}),
  importPreview: (rows: ImportPreviewRow[]) =>
    api.post<ImportPreviewResponse>('/maintenance-tasks/import-preview', { rows }),
  importCommit:  (rows: ImportCommitRow[]) =>
    api.post<ImportCommitResponse>('/maintenance-tasks/import-commit',  { rows }),
};

// ── Work Orders ───────────────────────────────────────────────────────────────
export interface WorkOrderPart {
  work_order_id: string;
  part_id: string;
  quantity_used: number;
  part?: Pick<Part, 'id' | 'part_number' | 'name' | 'unit_cost'>;
}

export interface WorkOrder {
  id: string; wo_number: string; title: string;
  description?: string | null;
  status: string; priority: string; equipment_id?: string | null;
  is_planned?: boolean;
  custom_equipment_name?: string | null;
  assigned_technician_id?: string | null;
  maintenance_task_id?: string | null;
  scheduled_start?: string | null; scheduled_end?: string | null;
  actual_start?: string | null;   actual_end?: string | null;
  labor_hours?: number | null; downtime_minutes?: number | null; cost?: number | null;
  completion_notes?: string | null;
  /** For meter-triggered auto-WOs: the meter reading that crossed the threshold. */
  trigger_meter_value?: number | string | null;
  tenant_id: string; created_by: string; created_at: string; updated_at: string;
  equipment?: Pick<Equipment, 'id' | 'asset_tag' | 'name' | 'location' | 'current_reading' | 'meter_unit'>;
  assigned_technician?: Pick<User, 'id' | 'full_name' | 'email'>;
  maintenance_task?: Pick<MaintenanceTask, 'id' | 'name' | 'task_type' | 'schedule_type' | 'meter_interval'>;
  parts?: WorkOrderPart[];
}

export const workOrdersApi = {
  list:    (page = 1, page_size = 50, status?: string, priority?: string, search?: string, part_id?: string) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    if (status)   params.set('status', status);
    if (priority) params.set('priority', priority);
    if (search)   params.set('search', search);
    if (part_id)  params.set('part_id', part_id);
    return api.get<Paginated<WorkOrder>>(`/work-orders?${params}`);
  },
  get:     (id: string) => api.get<WorkOrder>(`/work-orders/${id}`),
  create:  (body: Partial<WorkOrder>) => api.post<WorkOrder>('/work-orders', body),
  update:  (id: string, body: Partial<WorkOrder>) => api.patch<WorkOrder>(`/work-orders/${id}`, body),
  delete:  (id: string) => api.delete<void>(`/work-orders/${id}`),
  signOff: (
    id: string,
    note: string,
    meterReading?: number,
    laborHours?: number,
    downtimeMinutes?: number,
  ) =>
    api.post<WorkOrder>(`/work-orders/${id}/sign-off`, {
      completion_notes: note,
      ...(meterReading    != null ? { meter_reading_value: meterReading } : {}),
      ...(laborHours      != null ? { labor_hours: laborHours }            : {}),
      ...(downtimeMinutes != null ? { downtime_minutes: downtimeMinutes }  : {}),
    }),
  approve: (id: string) => api.post<WorkOrder>(`/work-orders/${id}/approve`, {}),
  reject:  (id: string, reason: string) =>
    api.post<WorkOrder>(`/work-orders/${id}/reject`, { rejection_reason: reason }),
  addPart:    (id: string, part_id: string, quantity_used: number) =>
    api.post<WorkOrderPart>(`/work-orders/${id}/parts`, { part_id, quantity_used }),
  removePart: (id: string, part_id: string) =>
    api.delete<void>(`/work-orders/${id}/parts/${part_id}`),
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
export interface DashboardKpis {
  total_equipment: number;
  total_schedules: number;
  total_parts: number;
  low_stock_parts: number;
  open_work_orders: number;
  overdue_tasks: number;
  awaiting_approval: number;
}

export interface DashboardData {
  kpis: DashboardKpis;
  wo_by_status: Record<string, number>;
  recent_work_orders: WorkOrder[];
  upcoming_tasks: (MaintenanceTask & { equipment?: Pick<Equipment, 'name'> })[];
}

export const dashboardApi = {
  get: () => api.get<DashboardData>('/dashboard'),
};

// ── Reports ───────────────────────────────────────────────────────────────────
export interface DowntimeSummary {
  total_downtime_minutes: number;
  total_incidents: number;
  total_cost: number;
  avg_downtime_per_incident: number;
}
export interface DowntimeByEquipment {
  equipment_id: string; equipment_name: string; asset_tag: string;
  downtime_minutes: number; incidents: number;
}
export interface DowntimeByMonth {
  month: string; downtime_minutes: number; incidents: number;
}
export interface DowntimeByTaskType {
  task_type: string; downtime_minutes: number; incidents: number;
}
export interface DowntimeLogEntry {
  id: string; wo_number: string; title: string;
  equipment_name: string; asset_tag: string;
  task_type: string | null;
  actual_start: string | null; actual_end: string | null;
  downtime_minutes: number; cost: number | null; status: string;
}
export interface DowntimeReport {
  summary: DowntimeSummary;
  by_equipment: DowntimeByEquipment[];
  by_month: DowntimeByMonth[];
  by_task_type: DowntimeByTaskType[];
  log: DowntimeLogEntry[];
}

export interface KpiReport {
  period:             { start_date: string; end_date: string };
  total_work_orders:  number;
  mtbf_hours:         number | null;
  mttr_hours:         number | null;
  pm_compliance_pct:  number | null;
  planned_count:      number;
  reactive_count:     number;
  planned_pct:        number | null;
  top_offenders:      { equipment_id: string; name: string; asset_tag: string; count: number }[];
}

export type KpiMetric = 'mtbf' | 'mttr' | 'pm_compliance' | 'total_wo';

export interface KpiMtbfRow {
  equipment_id:    string;
  name:            string;
  asset_tag:       string;
  failure_count:   number;
  avg_gap_hours:   number;
  last_gap_hours:  number;
  last_failure_at: string;
}
export interface KpiMttrRow {
  wo_id:          string;
  wo_number:      string;
  title:          string;
  equipment_id:   string | null;
  equipment_name: string;
  asset_tag:      string;
  created_at:     string;
  actual_end:     string;
  duration_hours: number;
  status:         string;
}
export interface KpiPmComplianceRow {
  wo_id:          string;
  wo_number:      string;
  title:          string;
  equipment_id:   string | null;
  equipment_name: string;
  asset_tag:      string;
  scheduled_end:  string | null;
  actual_end:     string;
  was_on_time:    boolean;
  days_late:      number;
  status:         string;
}
export interface KpiTotalWoRow {
  wo_id:          string;
  wo_number:      string;
  title:          string;
  equipment_id:   string | null;
  equipment_name: string;
  asset_tag:      string;
  status:         string;
  priority:       string;
  task_type:      string;
  created_at:     string;
  actual_end:     string | null;
}

export type KpiDetailsResponse =
  | { metric: 'mtbf';          rows: KpiMtbfRow[] }
  | { metric: 'mttr';          rows: KpiMttrRow[] }
  | { metric: 'pm_compliance'; rows: KpiPmComplianceRow[] }
  | { metric: 'total_wo';      rows: KpiTotalWoRow[] };

export const reportsApi = {
  downtime: (params: { start_date?: string; end_date?: string; equipment_id?: string } = {}) => {
    const p = new URLSearchParams();
    if (params.start_date)   p.set('start_date',   params.start_date);
    if (params.end_date)     p.set('end_date',     params.end_date);
    if (params.equipment_id) p.set('equipment_id', params.equipment_id);
    return api.get<DowntimeReport>(`/reports/downtime?${p}`);
  },
  kpis: (params: { start_date?: string; end_date?: string } = {}) => {
    const p = new URLSearchParams();
    if (params.start_date) p.set('start_date', params.start_date);
    if (params.end_date)   p.set('end_date',   params.end_date);
    return api.get<KpiReport>(`/reports/kpis?${p}`);
  },
  kpiDetails: (metric: KpiMetric, params: { start_date?: string; end_date?: string } = {}) => {
    const p = new URLSearchParams({ metric });
    if (params.start_date) p.set('start_date', params.start_date);
    if (params.end_date)   p.set('end_date',   params.end_date);
    return api.get<KpiDetailsResponse>(`/reports/kpi-details?${p}`);
  },
};

// ── Maintenance History ───────────────────────────────────────────────────────
export const historyApi = {
  list: (page = 1, page_size = 50, equipment_id?: string) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    if (equipment_id) params.set('equipment_id', equipment_id);
    return api.get<Paginated<WorkOrder>>(`/maintenance-history?${params}`);
  },
};

// ── Notifications ─────────────────────────────────────────────────────────────
export interface Notification {
  id: string; title: string; body?: string; severity: string;
  is_read: boolean; user_id: string; tenant_id: string;
  entity_type?: string | null; entity_id?: string | null;
  link_url?: string | null; created_at: string;
}

export const notificationsApi = {
  list:    (page = 1, is_read?: boolean) => {
    const params = new URLSearchParams({ page: String(page), page_size: '20' });
    if (is_read !== undefined) params.set('is_read', String(is_read));
    return api.get<Paginated<Notification>>(`/notifications?${params}`);
  },
  read:    (id: string) => api.post<Notification>(`/notifications/${id}/read`, {}),
  readAll: () => api.post<{ updated: number }>('/notifications/read-all', {}),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export interface User {
  id: string; email: string; full_name: string; role: Role;
  is_active: boolean; tenant_id: string; created_at: string; updated_at: string;
  email_notifications?: boolean;
}

export const usersApi = {
  list:   (page = 1) =>
    api.get<Paginated<User>>(`/users?page=${page}&page_size=50`),
  create: (body: { email: string; password: string; full_name: string; role: Role }) =>
    api.post<User>('/users', body),
  update: (id: string, body: Partial<{ full_name: string; role: Role; is_active: boolean; password: string }>) =>
    api.patch<User>(`/users/${id}`, body),
  updateMe: (body: { full_name?: string; email_notifications?: boolean; current_password?: string; new_password?: string }) =>
    api.patch<User>('/users/me', body),
};

// ── Audit Log ─────────────────────────────────────────────────────────────────
export interface AuditEntry {
  id: string; action: string; entity_type?: string; entity_id?: string;
  details?: Record<string, unknown>; ip_address?: string;
  created_at: string;
  user?: Pick<User, 'id' | 'full_name' | 'email'>;
}

export const auditApi = {
  list: (page = 1, entity_type?: string) => {
    const params = new URLSearchParams({ page: String(page), page_size: '50' });
    if (entity_type) params.set('entity_type', entity_type);
    return api.get<Paginated<AuditEntry>>(`/audit-log?${params}`);
  },
};

// ── Demo Request ─────────────────────────────────────────────────────────────
export const demoApi = {
  requestDemo: (data: {
    full_name: string;
    company_name: string;
    email: string;
    phone?: string;
    message?: string;
  }) => api.post<{ ok: boolean }>('/demo-request', data),
};

// ── License ───────────────────────────────────────────────────────────────────
export interface LicenseStatus {
  plan:           string;
  tier:           string | null;
  expiresAt:      string | null;
  graceDays:      number | null;
  warningDays?:   number | null;
  warningType?:   string | null;
  billing_email?: string | null;
  has_active_sub?: boolean;
  /** Active-user cap from on-prem license. Null = unlimited (SaaS or Enterprise). */
  max_users?:     number | null;
  /** Current active-user count. Only populated when max_users is set. */
  current_users?: number | null;
}

export const licenseApi = {
  status:   ()            => api.get<LicenseStatus>('/license/status'),
  activate: (key: string) => api.post<{ plan: string; expiresAt: string }>('/license/activate', { key }),
};

// ── Billing (Stripe) ─────────────────────────────────────────────────────────
export type BillingTier  = 'standard' | 'pro';
export type BillingCycle = 'monthly'  | 'annual';

export const billingApi = {
  checkout: (tier: BillingTier, cycle: BillingCycle) =>
    api.post<{ url: string }>('/stripe/checkout', { tier, cycle }),
  portal:   () => api.post<{ url: string }>('/stripe/portal', {}),
};

export interface TenantSettings {
  name: string;
  timezone: string;
  pm_compliance_window_days: number;
}

export const settingsApi = {
  get:    () => api.get<TenantSettings>('/settings'),
  update: (body: Partial<Pick<TenantSettings, 'pm_compliance_window_days'>>) =>
    api.patch<TenantSettings>('/settings', body),
};

// ── Attachments ───────────────────────────────────────────────────────────────

export type AttachmentEntityType = 'equipment' | 'work_order' | 'part';

export interface Attachment {
  id:          string;
  tenant_id:   string;
  entity_type: AttachmentEntityType;
  entity_id:   string;
  filename:    string;
  storage_key: string;
  file_size:   number;
  mime_type:   string;
  uploaded_by: string;
  created_at:  string;
}

export const attachmentsApi = {
  list: (entityType: AttachmentEntityType, entityId: string) =>
    api.get<Attachment[]>(`/attachments?entity_type=${entityType}&entity_id=${entityId}`),

  upload: async (entityType: AttachmentEntityType, entityId: string, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    // Cannot use api.post — it hardcodes Content-Type: application/json which breaks multipart
    const res = await fetch('/api/v1/attachments', {
      method:      'POST',
      credentials: 'include',
      body:        formData,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(res.status, (data as { error?: string }).error ?? 'Upload failed');
    }
    return res.json() as Promise<Attachment>;
  },

  fileUrl: (id: string) => `/api/v1/attachments/${id}/file`,

  delete: (id: string) => api.delete<void>(`/attachments/${id}`),
};
